// ─────────────────────────────────────────────────────────────────────────────
// Census Bureau Geographic Lookup
// Fetches counties and places (cities/towns) by state from the
// Census TIGER Web MapServer — free, no API key, browser CORS-safe.
//
// NOTE: The Census Data API (api.census.gov/data/...) now requires an API key
// and can no longer be used from the browser without one. TIGER web services
// (tigerweb.geo.census.gov) remain free and key-less.
//
// Results are cached in localStorage per state FIPS with a 30-day TTL.
// ─────────────────────────────────────────────────────────────────────────────

const TIGER_BASE = 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb';
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface GeoState {
  name: string;   // "Georgia"
  abbr: string;   // "GA"
  fips: string;   // "13"
}

export interface GeoEntry {
  name: string;   // "Fulton County" / "Atlanta"
  fips: string;   // county or place FIPS code
}

// ── Static state list ─────────────────────────────────────────────────────────
export const US_STATES: GeoState[] = [
  { name: 'Alabama',              abbr: 'AL', fips: '01' },
  { name: 'Alaska',               abbr: 'AK', fips: '02' },
  { name: 'Arizona',              abbr: 'AZ', fips: '04' },
  { name: 'Arkansas',             abbr: 'AR', fips: '05' },
  { name: 'California',           abbr: 'CA', fips: '06' },
  { name: 'Colorado',             abbr: 'CO', fips: '08' },
  { name: 'Connecticut',          abbr: 'CT', fips: '09' },
  { name: 'Delaware',             abbr: 'DE', fips: '10' },
  { name: 'District of Columbia', abbr: 'DC', fips: '11' },
  { name: 'Florida',              abbr: 'FL', fips: '12' },
  { name: 'Georgia',              abbr: 'GA', fips: '13' },
  { name: 'Hawaii',               abbr: 'HI', fips: '15' },
  { name: 'Idaho',                abbr: 'ID', fips: '16' },
  { name: 'Illinois',             abbr: 'IL', fips: '17' },
  { name: 'Indiana',              abbr: 'IN', fips: '18' },
  { name: 'Iowa',                 abbr: 'IA', fips: '19' },
  { name: 'Kansas',               abbr: 'KS', fips: '20' },
  { name: 'Kentucky',             abbr: 'KY', fips: '21' },
  { name: 'Louisiana',            abbr: 'LA', fips: '22' },
  { name: 'Maine',                abbr: 'ME', fips: '23' },
  { name: 'Maryland',             abbr: 'MD', fips: '24' },
  { name: 'Massachusetts',        abbr: 'MA', fips: '25' },
  { name: 'Michigan',             abbr: 'MI', fips: '26' },
  { name: 'Minnesota',            abbr: 'MN', fips: '27' },
  { name: 'Mississippi',          abbr: 'MS', fips: '28' },
  { name: 'Missouri',             abbr: 'MO', fips: '29' },
  { name: 'Montana',              abbr: 'MT', fips: '30' },
  { name: 'Nebraska',             abbr: 'NE', fips: '31' },
  { name: 'Nevada',               abbr: 'NV', fips: '32' },
  { name: 'New Hampshire',        abbr: 'NH', fips: '33' },
  { name: 'New Jersey',           abbr: 'NJ', fips: '34' },
  { name: 'New Mexico',           abbr: 'NM', fips: '35' },
  { name: 'New York',             abbr: 'NY', fips: '36' },
  { name: 'North Carolina',       abbr: 'NC', fips: '37' },
  { name: 'North Dakota',         abbr: 'ND', fips: '38' },
  { name: 'Ohio',                 abbr: 'OH', fips: '39' },
  { name: 'Oklahoma',             abbr: 'OK', fips: '40' },
  { name: 'Oregon',               abbr: 'OR', fips: '41' },
  { name: 'Pennsylvania',         abbr: 'PA', fips: '42' },
  { name: 'Rhode Island',         abbr: 'RI', fips: '44' },
  { name: 'South Carolina',       abbr: 'SC', fips: '45' },
  { name: 'South Dakota',         abbr: 'SD', fips: '46' },
  { name: 'Tennessee',            abbr: 'TN', fips: '47' },
  { name: 'Texas',                abbr: 'TX', fips: '48' },
  { name: 'Utah',                 abbr: 'UT', fips: '49' },
  { name: 'Vermont',              abbr: 'VT', fips: '50' },
  { name: 'Virginia',             abbr: 'VA', fips: '51' },
  { name: 'Washington',           abbr: 'WA', fips: '53' },
  { name: 'West Virginia',        abbr: 'WV', fips: '54' },
  { name: 'Wisconsin',            abbr: 'WI', fips: '55' },
  { name: 'Wyoming',              abbr: 'WY', fips: '56' },
  { name: 'Puerto Rico',          abbr: 'PR', fips: '72' },
];

export const STATE_BY_ABBR = new Map<string, GeoState>(US_STATES.map(s => [s.abbr, s]));
export const STATE_BY_FIPS = new Map<string, GeoState>(US_STATES.map(s => [s.fips, s]));

// ── Cache helpers ─────────────────────────────────────────────────────────────
interface CacheEntry<T> { data: T; expires: number }

function cacheGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() > entry.expires) { localStorage.removeItem(key); return null; }
    return entry.data;
  } catch { return null; }
}

function cacheSet<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, expires: Date.now() + CACHE_TTL_MS };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch { /* storage full — ignore */ }
}

// ── TIGER web fetch helpers ───────────────────────────────────────────────────

interface TIGERResponse {
  features?: Array<{ attributes: Record<string, string> }>;
  error?: { code: number; message: string };
}

/** Fetch county names from TIGER State_County MapServer (no API key required). */
async function fetchTIGERCounties(stateFips: string): Promise<GeoEntry[]> {
  const url =
    `${TIGER_BASE}/State_County/MapServer/1/query` +
    `?where=STATE+%3D+%27${stateFips}%27&outFields=NAME%2CCOUNTY&returnGeometry=false&f=json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`TIGER counties ${res.status}`);
  const data: TIGERResponse = await res.json();
  if (data.error) throw new Error(`TIGER counties: ${data.error.message}`);
  return (data.features ?? [])
    .map(f => ({ name: f.attributes.NAME ?? '', fips: f.attributes.COUNTY ?? '' }))
    .filter(e => e.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Fetch incorporated place names from TIGER Places MapServer (no API key required). */
async function fetchTIGERPlaces(stateFips: string): Promise<GeoEntry[]> {
  // Layer 18 = Incorporated Places (ACS 2025 vintage); supports 100k records per query
  const url =
    `${TIGER_BASE}/Places_CouSub_ConCity_SubMCD/MapServer/18/query` +
    `?where=STATE+%3D+%27${stateFips}%27&outFields=NAME%2CPLACE&returnGeometry=false&f=json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`TIGER places ${res.status}`);
  const data: TIGERResponse = await res.json();
  if (data.error) throw new Error(`TIGER places: ${data.error.message}`);

  // Strip type suffixes: "Miami city" → "Miami", "Surfside town" → "Surfside"
  const seen = new Set<string>();
  return (data.features ?? [])
    .map(f => {
      const raw = f.attributes.NAME ?? '';
      const clean = raw
        .replace(/\s+(city|town|village|CDP|borough|township|municipality|plantation|gore|grant|location|unorganized territory)$/i, '')
        .trim();
      return { name: clean || raw, fips: f.attributes.PLACE ?? '' };
    })
    .filter(e => {
      if (!e.name || seen.has(e.name)) return false;
      seen.add(e.name);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface StateGeoData {
  counties: GeoEntry[];
  places: GeoEntry[];
}

// ── County boundary polygons ──────────────────────────────────────────────────

export interface CountyFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: unknown;
    properties: { NAME: string; STATE: string; COUNTY: string };
  }>;
}

/**
 * Fetch GeoJSON boundary polygons for specific counties in a state.
 * Uses Census TIGER web services — free, no key, browser CORS-safe.
 * Results cached in localStorage per state FIPS with a 30-day TTL.
 */
export async function fetchCountyBoundaries(
  stateAbbr: string,
  countyNames: string[],
): Promise<CountyFeatureCollection> {
  const state = STATE_BY_ABBR.get(stateAbbr.toUpperCase());
  if (!state || countyNames.length === 0) return { type: 'FeatureCollection', features: [] };

  const cacheKey = `census_county_geo_v2_${state.fips}`;
  let allFeatures = cacheGet<CountyFeatureCollection['features']>(cacheKey);

  if (!allFeatures) {
    const url =
      `${TIGER_BASE}/State_County/MapServer/1/query` +
      `?where=STATE%3D'${state.fips}'&outFields=NAME%2CSTATE%2CCOUNTY&returnGeometry=true&f=geojson`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`TIGER ${res.status}`);
    const data = await res.json() as CountyFeatureCollection;
    allFeatures = data.features ?? [];
    cacheSet(cacheKey, allFeatures);
  }

  // Filter to just the requested county names (case-insensitive, strip "County" suffix for matching)
  const normalize = (s: string) => s.toLowerCase().replace(/\s+county$/i, '').trim();
  const requested = new Set(countyNames.map(normalize));
  const filtered = allFeatures.filter(f => requested.has(normalize(f.properties.NAME)));

  return { type: 'FeatureCollection', features: filtered };
}

/**
 * Fetch incorporated places that fall within the bounding box of the given counties.
 * Used to narrow the city picker after counties are selected.
 * Results are NOT cached (county selection changes too often to benefit from caching).
 */
export async function fetchPlacesForCounties(
  stateAbbr: string,
  countyFipsList: string[],
): Promise<GeoEntry[]> {
  const state = STATE_BY_ABBR.get(stateAbbr.toUpperCase());
  if (!state || !countyFipsList.length) return [];

  // Step 1: get combined bounding box of all selected counties (WGS84)
  const countyIn = countyFipsList.map(f => `'${f}'`).join(',');
  const extentUrl =
    `${TIGER_BASE}/State_County/MapServer/1/query` +
    `?where=${encodeURIComponent(`STATE='${state.fips}' AND COUNTY IN (${countyIn})`)}&returnExtentOnly=true&outSR=4326&f=json`;
  const extentRes = await fetch(extentUrl);
  if (!extentRes.ok) throw new Error(`TIGER extent ${extentRes.status}`);
  const extentData = await extentRes.json() as { extent?: { xmin: number; ymin: number; xmax: number; ymax: number } };
  const ext = extentData.extent;
  if (!ext) return [];

  // Step 2: spatially query incorporated places that intersect the county bounding box
  const geometry = JSON.stringify({
    xmin: ext.xmin, ymin: ext.ymin, xmax: ext.xmax, ymax: ext.ymax,
    spatialReference: { wkid: 4326 },
  });
  const placesUrl =
    `${TIGER_BASE}/Places_CouSub_ConCity_SubMCD/MapServer/18/query` +
    `?where=${encodeURIComponent(`STATE='${state.fips}'`)}` +
    `&geometry=${encodeURIComponent(geometry)}&geometryType=esriGeometryEnvelope&spatialRel=esriSpatialRelIntersects` +
    `&outFields=NAME%2CPLACE&returnGeometry=false&f=json`;
  const placesRes = await fetch(placesUrl);
  if (!placesRes.ok) throw new Error(`TIGER places filter ${placesRes.status}`);
  const placesData: TIGERResponse = await placesRes.json();
  if (placesData.error) throw new Error(`TIGER places filter: ${placesData.error.message}`);

  const seen = new Set<string>();
  return (placesData.features ?? [])
    .map(f => {
      const raw = f.attributes.NAME ?? '';
      const clean = raw
        .replace(/\s+(city|town|village|CDP|borough|township|municipality|plantation|gore|grant|location|unorganized territory)$/i, '')
        .trim();
      return { name: clean || raw, fips: f.attributes.PLACE ?? '' };
    })
    .filter(e => {
      if (!e.name || seen.has(e.name)) return false;
      seen.add(e.name);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Fetch (or return cached) counties + places for a state abbreviation. */
export async function fetchStateGeo(stateAbbr: string): Promise<StateGeoData> {
  const state = STATE_BY_ABBR.get(stateAbbr.toUpperCase());
  if (!state) return { counties: [], places: [] };

  // Bump cache key to v2 — invalidates old Census API cached data
  const cacheKey = `tiger_geo_v2_${state.fips}`;
  const cached = cacheGet<StateGeoData>(cacheKey);
  if (cached) return cached;

  const [counties, places] = await Promise.all([
    fetchTIGERCounties(state.fips),
    fetchTIGERPlaces(state.fips),
  ]);

  const result: StateGeoData = { counties, places };
  cacheSet(cacheKey, result);
  return result;
}
